import React from 'react';
import { useOrion } from '../context/OrionContext';
import Icon from './Icon';
import PilulaAtividade from './PilulaAtividade';
import SinoNotificacoes from './SinoNotificacoes';
import { notasDaVersao, notasJaVistas } from '../services/notasVersao';

// ─────────────────────────────────────────────────────────────────────────────
// Topbar — mesma estrutura da do Nexus.
//
// Esquerda: botão de menu (só na janela estreita), título da tela e a versão
// em miúdo. Direita: quem está logado e as ações de ícone.
//
// Os controles da JANELA (minimizar, maximizar, fechar) não estão aqui: ficam
// na barra de título, acima de tudo — ver components/BarraJanela.js.
// ─────────────────────────────────────────────────────────────────────────────

/** Título e linha de contexto de cada tela — equivalente ao VIEW_META do Nexus. */
export const VIEW_META = {
  gerencia: { titulo: 'Filas do setor', eyebrow: 'O que existe agora, e o que está livre para pegar', icon: 'inbox' },
  helpdesk: { titulo: 'Minha Área', eyebrow: 'Seus tickets e o atendimento, do texto ao envio', icon: 'send' },
  'busca-ticket': { titulo: 'Consultar ticket', eyebrow: 'A ficha e as interações de qualquer ticket, pelo número', icon: 'search' },
  'formalizacao-rapida': { titulo: 'Formalização rápida', eyebrow: 'Seu texto, formalizado — sem ticket', icon: 'zap' },
  atividade: { titulo: 'Atividade', eyebrow: 'Seu histórico de status e pausas', icon: 'activity' },
  incidentes: { titulo: 'Incidentes', eyebrow: 'Registro e busca de erros conhecidos', icon: 'bug' },
  'chamado-ti': { titulo: 'Chamado T.I.', eyebrow: 'Suporte interno da equipe', icon: 'monitor' },
  historico: { titulo: 'Histórico', eyebrow: 'Formalizações que passaram por aqui', icon: 'history' },
  configuracoes: { titulo: 'Configurações', eyebrow: 'Preferências do Orion neste computador', icon: 'settings' },
  zarion: { titulo: 'Zarion', eyebrow: 'Base de conhecimento da Space, aberta aqui dentro', icon: 'compass' },
  notas: { titulo: 'Notas de versão', eyebrow: 'O que mudou no Orion', icon: 'release-notes' },
  notificacoes: { titulo: 'Notificações', eyebrow: 'Sua caixa de entrada — clique para abrir o ticket', icon: 'bell' },
};

/** Iniciais do operador para o avatar. */
function iniciaisDe(nome) {
  const partes = String(nome || '').trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

export default function Topbar({ aoAbrirMenu, menuAberto = false }) {
  const {
    view, setView, versao, tema, trocarTema, operador,
    updatePronto, instalarUpdate, estadoUpdate, verificarUpdate, ehDesktop,
  } = useOrion();

  // Relido a cada render: a tela de notas marca como vistas ao abrir, e a
  // troca de tela já refaz a barra — sem estado para sincronizar.
  const novidades = view !== 'notas' && !!notasDaVersao(versao) && !notasJaVistas(versao);

  const fase = estadoUpdate?.fase;
  const buscando = fase === 'verificando' || fase === 'baixando';
  const tituloVerificar = fase === 'verificando'
    ? 'Verificando atualizações…'
    : fase === 'baixando'
      ? `Baixando a versão ${estadoUpdate?.versao || 'nova'}${estadoUpdate?.percentual != null ? ` — ${estadoUpdate.percentual}%` : ''}`
      : 'Verificar atualizações';

  const meta = VIEW_META[view] || VIEW_META.helpdesk;
  const escuro = tema === 'dark';
  const alternarTema = () => trocarTema(escuro ? 'light' : 'dark');

  return (
    <header className="topbar janela-arrasta">
      <div className="topbar-title">
        <button
          className="menu-btn no-drag"
          onClick={aoAbrirMenu}
          aria-label="Abrir menu"
          aria-expanded={menuAberto}
        >
          <Icon name="menu" size={18} />
        </button>
        {meta.titulo}
        {/* O NÚMERO DA VERSÃO ABRE AS NOTAS DELA — como no Nexus. É onde a
            pessoa já olha para saber em que versão está. O ponto aceso diz que
            a versão em uso tem notas que ainda não foram abertas neste
            computador: é o "tem novidade" de quem acabou de atualizar. */}
        {versao && (
          <button
            type="button"
            className={`tb-versao no-drag ${view === 'notas' ? 'ativa' : ''}`}
            onClick={() => setView('notas')}
            title={novidades ? `Novidades da versão ${versao} — ver as notas` : `Ver as notas da versão ${versao}`}
          >
            v{versao}
            {novidades && <span className="tb-versao-ponto" aria-label="novidades" />}
          </button>
        )}
      </div>

      <div className="topbar-right">
        {/* Status do operador — some para quem não está sob controle. */}
        <PilulaAtividade />

        {/* Sininho, como o do Nexus: painel suspenso com as recentes. */}
        <SinoNotificacoes />

        <div className="who">
          <div className="who-name">{operador?.nome}</div>
          <div className="who-team">
            <Icon name="user" size={11} />
            {operador?.codigo ? ` Operador ${operador.codigo}` : ' Sem cadastro'}
          </div>
        </div>

        <div className="topbar-avatar" title={operador?.email}>{iniciaisDe(operador?.nome)}</div>

        <div className="top-actions">
          {/* Só acende quando há atualização baixada esperando reinício. Quem
              decide a hora de reiniciar é o operador — nada é instalado no
              meio de um atendimento. */}
          {/* Fora disso, o mesmo lugar é o "Verificar atualizações": procura
              na hora e, achando, baixa em segundo plano. Só no desktop — na
              web quem atualiza é o servidor. */}
          {updatePronto ? (
            <button
              className="icon-btn no-drag"
              onClick={instalarUpdate}
              title="Atualização disponível — instalar e reiniciar"
              style={{ color: 'var(--ok)' }}
            >
              <Icon name="cloud-download" size={16} />
            </button>
          ) : ehDesktop && (
            <button
              className="icon-btn no-drag"
              onClick={verificarUpdate}
              disabled={buscando}
              title={tituloVerificar}
              aria-label={tituloVerificar}
              style={buscando ? { opacity: 0.55, cursor: 'progress' } : undefined}
            >
              <Icon name="cloud-download" size={16} />
            </button>
          )}

          <button className="icon-btn no-drag" onClick={alternarTema} title="Tema claro/escuro">
            <Icon name={escuro ? 'sun' : 'moon'} size={16} />
          </button>

          {/* Minimizar, maximizar e fechar NÃO ficam aqui: são controles da
              JANELA e vivem na barra de título (components/BarraJanela.js),
              acima de tudo — igual ao Nexus. Misturá-los com as ações do app
              faria o operador fechar a janela querendo fechar um painel. */}
        </div>
      </div>
    </header>
  );
}
